import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useMemo, useState } from 'react'
import HammockDialog from '../v2/components/Dialog/HammockDialog'
import HammockFormDialog from '../v2/components/Dialog/HammockFormDialog'
import { TimeMutationDto, TimeOverviewDto } from '../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../pages/BaseMutableOverview/BaseMutableOverview'
import TimeSuggestionMutationDialogBody, { TrackTimeSuggestionDto } from '../v2/components/Dialog/Registration/TimeSuggestionMutationDialogBody'
import TimeMutationDialogBody from '../v2/components/Dialog/Registration/TimeMutationDialogBody'
import TaskMutationDialogBody, { getNewTaskValues, TaskDialogBodyProps } from '../v2/components/Dialog/Task/TaskMutationDialogBody'
import useTasks from '../hooks/useTasks'
import EmailFilingSuggestionDialogBody, { EmailFilingSuggestionDto } from '../v2/components/Dialog/ProcessEmail/EmailFilingSuggestionDialogBody'
import { TaskMutationDto } from '../config/taskConfig'
import useUser from '../hooks/useUser'
import useTaskStatuses from '../hooks/useTaskStatuses'

export type PopupContextType = {
    showTimeSuggestionDialog: boolean
    setShowTimeSuggestionDialog: (item: Partial<TrackTimeSuggestionDto>) => void
    cancelTimeSuggestionDialog: (id: string) => void
    showTimeTrackingDialog: boolean
    setShowTimeTrackingDialog: (item: Partial<TimeMutationDto>) => void
    showTaskDialog: boolean
    /**
     * Opens the add-a-task popup on top of whatever the user is looking at (HAM-4275). Only for screens without a task
     * overview of their own; where there is one, its own dialog is used through BaseMutableOverview.
     */
    setShowTaskDialog: (item: Partial<TaskMutationDto>) => void
    showEmailFilingDialog: boolean
    /**
     * Opens the file-this-e-mail popup for a suggestion from the inboxer (HAM-4278). Raised by the websocket
     * notification, so there is never a screen behind it that could host the dialog itself.
     */
    setShowEmailFilingDialog: (item: EmailFilingSuggestionDto) => void
}

const PopupContext = createContext<PopupContextType>(null)

export function PopupProvider(props: PropsWithChildren): ReactElement {
    const [showTimeSuggestionDialog, setShowTimeSuggestionDialog] = useState<Partial<TrackTimeSuggestionDto>>(undefined)
    const [showTimeTrackingDialog, setShowTimeTrackingDialog] = useState<Partial<TimeMutationDto>>(undefined)
    const [showTaskDialog, setShowTaskDialog] = useState<Partial<TaskMutationDto>>(undefined)
    const [showEmailFilingDialog, setShowEmailFilingDialog] = useState<EmailFilingSuggestionDto>(undefined)
    const { currentUser } = useUser()
    const { getStartStatus } = useTaskStatuses()
    const { dateConfig } = useTasks()

    const cancelTimeSuggestionDialog = useMemo(
        () => (id: string) => {
            //Only cancel the time suggestion when the ids match
            if (showTimeSuggestionDialog?.timeSuggestionId === id) {
                setShowTimeSuggestionDialog(undefined)
            }
        },
        [showTimeSuggestionDialog],
    )

    return (
        <PopupContext.Provider
            value={{
                showTimeSuggestionDialog: showTimeSuggestionDialog !== undefined,
                setShowTimeSuggestionDialog,
                cancelTimeSuggestionDialog,
                showTimeTrackingDialog: showTimeTrackingDialog !== undefined,
                setShowTimeTrackingDialog,
                showTaskDialog: showTaskDialog !== undefined,
                setShowTaskDialog,
                showEmailFilingDialog: showEmailFilingDialog !== undefined,
                setShowEmailFilingDialog,
            }}
        >
            <>
                <div aria-label={'global-popups'}>
                    <HammockFormDialog<Partial<TrackTimeSuggestionDto>, BaseMutableOverviewDialogProps<TimeOverviewDto>>
                        open={showTimeSuggestionDialog !== undefined}
                        setOpen={open => setShowTimeSuggestionDialog(open ? showTimeSuggestionDialog : undefined)}
                        dialogFormProps={() => ({ initialValues: { ...showTimeSuggestionDialog }, onSubmit: () => {} })}
                        dialogFormBody={TimeSuggestionMutationDialogBody}
                        onDismiss={() => {
                            setShowTimeSuggestionDialog(undefined)
                        }}
                    />
                    <HammockFormDialog<Partial<TimeMutationDto>, BaseMutableOverviewDialogProps<TimeOverviewDto>>
                        open={showTimeTrackingDialog !== undefined}
                        setOpen={open => setShowTimeTrackingDialog(open ? showTimeTrackingDialog : undefined)}
                        dialogFormProps={() => ({ initialValues: { ...showTimeTrackingDialog }, onSubmit: () => {} })}
                        dialogFormBody={TimeMutationDialogBody}
                        warnOnUnsavedChanges
                        onDismiss={() => {
                            setShowTimeTrackingDialog(undefined)
                        }}
                    />
                    <HammockFormDialog<Partial<TaskMutationDto>, TaskDialogBodyProps>
                        open={showTaskDialog !== undefined}
                        setOpen={open => setShowTaskDialog(open ? showTaskDialog : undefined)}
                        dialogFormProps={() => ({
                            initialValues: getNewTaskValues(
                                {
                                    status: getStartStatus()?.alpha,
                                    assigneeId: currentUser?.id,
                                    assignerId: currentUser?.id,
                                    ...showTaskDialog,
                                },
                                dateConfig,
                            ),
                            onSubmit: () => {},
                        })}
                        dialogFormBody={TaskMutationDialogBody}
                        warnOnUnsavedChanges
                        onDismiss={() => {
                            setShowTaskDialog(undefined)
                        }}
                    />
                    <HammockDialog<EmailFilingSuggestionDto>
                        open={showEmailFilingDialog !== undefined}
                        setOpen={open => setShowEmailFilingDialog(open ? showEmailFilingDialog : undefined)}
                        dialogBody={EmailFilingSuggestionDialogBody}
                        dialogBodyProps={() => showEmailFilingDialog}
                        onDismiss={() => {
                            setShowEmailFilingDialog(undefined)
                        }}
                    />
                </div>
                {props.children}
            </>
        </PopupContext.Provider>
    )
}

export default PopupContext
